import {startReservedMatch, releaseReservedMatch, removeStaleReservedMatch} from './matchReservationLifecycle';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  where,
} from "firebase/firestore";

const MATCH_RESERVATION_STALE_MS = 15 * 60 * 1000;
const MATCH_RESERVATION_RESERVED_STATE = "reserved";

function normalizeReservationString(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function normalizeMatchReservationKeyTitle(title) {
  return normalizeReservationString(title).toLowerCase();
}

export function getCurrentLocalDateInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getCurrentLocalTimeInputValue() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function normalizeReservationTime(matchTime) {
  const trimmed = normalizeReservationString(matchTime);
  const [rawHours = "", rawMinutes = ""] = trimmed.split(":");
  const hours = String(Math.max(0, Math.min(23, Number(rawHours) || 0))).padStart(2, "0");
  const minutes = String(Math.max(0, Math.min(59, Number(rawMinutes) || 0))).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function buildMatchReservationKey(title, matchDate, matchTime, eventKey = "") {
  if (eventKey) return eventKey;
  const normalizedTitle = normalizeMatchReservationKeyTitle(title);
  const normalizedDate = normalizeReservationString(matchDate);
  const normalizedTime = normalizeReservationTime(matchTime);
  if (!normalizedTitle || !normalizedDate || !normalizedTime) return "";
  return `${normalizedTitle}__${normalizedDate}__${normalizedTime}`;
}

export function createMatchReservationSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `web-reservation-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isReservedMatchDoc(data) {
  return normalizeReservationString(data?.reservationState) === MATCH_RESERVATION_RESERVED_STATE;
}

function buildDeterministicMatchId(reservationKey) {
  const slug =
    reservationKey
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64) || "match";

  let hash = 0;
  for (let index = 0; index < reservationKey.length; index += 1) {
    hash = (hash * 31 + reservationKey.charCodeAt(index)) >>> 0;
  }

  return `${slug}-${hash.toString(36)}`;
}

function toMillis(value) {
  if (value && typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }

  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const millis = Date.parse(value);
    return Number.isFinite(millis) ? millis : 0;
  }
  return 0;
}

function toLocalDateInput(value) {
  const millis = toMillis(value);
  if (!millis) return "";

  const nextDate = new Date(millis);
  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, "0");
  const day = String(nextDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toLocalTimeInput(value) {
  const millis = toMillis(value);
  if (!millis) return "";

  const nextDate = new Date(millis);
  const hours = String(nextDate.getHours()).padStart(2, "0");
  const minutes = String(nextDate.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function matchDataFitsReservation(data, reservationKey, matchKey, matchDate, matchTime) {
  if (typeof data?.reservationKey === "string" && data.reservationKey === reservationKey) {
    return true;
  }

  return (
    typeof data?.matchKey === "string" &&
    data.matchKey === matchKey &&
    toLocalDateInput(data.matchPlayedAt) === matchDate &&
    toLocalTimeInput(data.matchPlayedAt) === matchTime
  );
}

function normalizeStringList(values) {
  return Array.from(
    new Set(
      Array.isArray(values)
        ? values
            .map((value) => (typeof value === "string" ? value.trim() : ""))
            .filter(Boolean)
        : [],
    ),
  );
}

function getMatchesCollection(db, teamId, mapId) {
  return collection(db, "teams", teamId, "maps", mapId, "matches");
}

function getMatchDocument(db, teamId, mapId, matchId) {
  return doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
}

function getMatchClaimDocument(db, teamId, mapId, matchId, sessionId) {
  return doc(db, "teams", teamId, "maps", mapId, "matches", matchId, "claims", sessionId);
}


async function findExactMatch({
  db,
  teamId,
  mapId,
  reservationKey,
  matchKey,
  matchDate,
  matchTime,
}) {
  const matchesCollection = getMatchesCollection(db, teamId, mapId);

  const reservationSnapshot = await getDocs(
    query(matchesCollection, where("reservationKey", "==", reservationKey), limit(1))
  );
  if (!reservationSnapshot.empty) {
    return reservationSnapshot.docs[0];
  }

  const legacySnapshot = await getDocs(
    query(matchesCollection, where("matchKey", "==", matchKey), limit(10))
  );

  return (
    legacySnapshot.docs.find((docSnap) =>
      matchDataFitsReservation(docSnap.data() || {}, reservationKey, matchKey, matchDate, matchTime)
    ) || null
  );
}

function buildReservationResult(matchId, reservationKey, claimActive, reservationState, title) {
  return {
    matchId,
    reservationKey,
    claimActive,
    reservationState,
    title: normalizeReservationString(title) || "Untitled match",
  };
}

export async function claimReservedMatch({
  db,
  teamId,
  mapId,
  matchId,
  identity,
}) {
  const matchRef = getMatchDocument(db, teamId, mapId, matchId);
  const claimRef = getMatchClaimDocument(db, teamId, mapId, matchId, identity.sessionId);

  return runTransaction(db, async (transaction) => {
    const matchSnap = await transaction.get(matchRef);
    if (!matchSnap.exists()) {
      throw new Error("That match is no longer available.");
    }

    const data = matchSnap.data() || {};
    const reservationState =
      typeof data.reservationState === "string" ? data.reservationState : null;
    const reservationKey =
      typeof data.reservationKey === "string" ? data.reservationKey : "";
    const title =
      typeof data.title === "string" && data.title.trim() ? data.title.trim() : "Untitled match";

    if (reservationState !== MATCH_RESERVATION_RESERVED_STATE) {
      return buildReservationResult(
        matchId,
        reservationKey,
        false,
        reservationState,
        title
      );
    }

    const now = Timestamp.now();
    transaction.set(
      claimRef,
      {
        sessionId: identity.sessionId,
        uid: identity.uid,
        username: identity.username,
        surface: identity.surface,
        status: "selected",
        createdAt: now,
        updatedAt: now,
      },
      { merge: true }
    );
    transaction.set(
      matchRef,
      {
        reservationLastHeartbeatAt: now,
      },
      { merge: true }
    );

    return buildReservationResult(
      matchId,
      reservationKey,
      true,
      MATCH_RESERVATION_RESERVED_STATE,
      title
    );
  });
}

export async function ensureMatchReservation({
  db,
  teamId,
  mapId,
  title,
  matchDate,
  matchTime,
  tags = [],
  comp = [],
  identity,
  eventKey = "",
}) {
  const normalizedTitle = normalizeReservationString(title);
  const normalizedDate = normalizeReservationString(matchDate);
  const normalizedTime = normalizeReservationTime(matchTime);
  const reservationKey = buildMatchReservationKey(
    normalizedTitle,
    normalizedDate,
    normalizedTime, eventKey
  );

  if (!reservationKey) {
    throw new Error("Match title, date, and time are required.");
  }

  const matchKey = normalizeMatchReservationKeyTitle(normalizedTitle);
  const existingExactMatch = eventKey ? null : await findExactMatch({
    db,
    teamId,
    mapId,
    reservationKey,
    matchKey,
    matchDate: normalizedDate,
    matchTime: normalizedTime,
  });

  if (existingExactMatch) {
    const data = existingExactMatch.data() || {};
    if (isReservedMatchDoc(data)) {
      return claimReservedMatch({
        db,
        teamId,
        mapId,
        matchId: existingExactMatch.id,
        identity,
      });
    }

    return buildReservationResult(
      existingExactMatch.id,
      reservationKey,
      false,
      typeof data.reservationState === "string" ? data.reservationState : null,
      typeof data.title === "string" ? data.title : normalizedTitle
    );
  }

  const matchesCollection = getMatchesCollection(db, teamId, mapId);
  const matchRef = doc(matchesCollection, buildDeterministicMatchId(reservationKey));
  const claimRef = getMatchClaimDocument(db, teamId, mapId, matchRef.id, identity.sessionId);
  const matchPlayedAt = Timestamp.fromDate(new Date(`${normalizedDate}T${normalizedTime}:00`));
  const cleanTags = normalizeStringList(tags);
  const cleanComp = normalizeStringList(comp);

  return runTransaction(db, async (transaction) => {
    const matchSnap = await transaction.get(matchRef);
    if (matchSnap.exists()) {
      const data = matchSnap.data() || {};
      const reservationState =
        typeof data.reservationState === "string" ? data.reservationState : null;
      const titleToUse =
        typeof data.title === "string" && data.title.trim() ? data.title.trim() : normalizedTitle;

      if (reservationState !== MATCH_RESERVATION_RESERVED_STATE) {
        return buildReservationResult(
          matchRef.id,
          reservationKey,
          false,
          reservationState,
          titleToUse
        );
      }

      const now = Timestamp.now();
      transaction.set(
        claimRef,
        {
          sessionId: identity.sessionId,
          uid: identity.uid,
          username: identity.username,
          surface: identity.surface,
          status: "selected",
          createdAt: now,
          updatedAt: now,
        },
        { merge: true }
      );
      transaction.set(
        matchRef,
        {
          title: normalizedTitle,
          matchKey,
          reservationKey,
          matchPlayedAt,
          reservationLastHeartbeatAt: now,
        },
        { merge: true }
      );

      return buildReservationResult(
        matchRef.id,
        reservationKey,
        true,
        MATCH_RESERVATION_RESERVED_STATE,
        normalizedTitle
      );
    }

    const now = Timestamp.now();
    transaction.set(matchRef, {
      title: normalizedTitle,
      matchKey,
      reservationKey,
      createdAt: now,
      createdBy: identity.username,
      createdByUid: identity.uid,
      matchPlayedAt,
      tags: cleanTags,
      comp: cleanComp,
      reservationState: MATCH_RESERVATION_RESERVED_STATE,
      reservedAt: now,
      reservedByUid: identity.uid,
      reservedByUsername: identity.username,
      reservationLastHeartbeatAt: now,
      reservationSurface: identity.surface,
    });
    transaction.set(claimRef, {
      sessionId: identity.sessionId,
      uid: identity.uid,
      username: identity.username,
      surface: identity.surface,
      status: "selected",
      createdAt: now,
      updatedAt: now,
    });

    return buildReservationResult(
      matchRef.id,
      reservationKey,
      true,
      MATCH_RESERVATION_RESERVED_STATE,
      normalizedTitle
    );
  });
}

export async function refreshMatchReservationHeartbeat({
  db,
  teamId,
  mapId,
  matchId,
  identity,
}) {
  const matchRef = getMatchDocument(db, teamId, mapId, matchId);
  const claimRef = getMatchClaimDocument(db, teamId, mapId, matchId, identity.sessionId);

  return runTransaction(db, async (transaction) => {
    const matchSnap = await transaction.get(matchRef);
    if (!matchSnap.exists()) return false;

    const data = matchSnap.data() || {};
    if (!isReservedMatchDoc(data)) return false;

    const now = Timestamp.now();
    transaction.set(
      claimRef,
      {
        sessionId: identity.sessionId,
        uid: identity.uid,
        username: identity.username,
        surface: identity.surface,
        status: "selected",
        updatedAt: now,
      },
      { merge: true }
    );
    transaction.set(
      matchRef,
      {
        reservationLastHeartbeatAt: now,
      },
      { merge: true }
    );

    return true;
  });
}

export async function markMatchReservationStarted(params) {
  return startReservedMatch(params);
}

export async function releaseMatchReservation(params) {
  return releaseReservedMatch(params);
}

export async function cleanupStaleMatchReservations({
  db,
  teamId,
  mapId,
}) {
  const matchesSnapshot = await getDocs(
    query(
      getMatchesCollection(db, teamId, mapId),
      where("reservationState", "==", MATCH_RESERVATION_RESERVED_STATE),
      limit(25)
    )
  );

  const now = Date.now();
  const staleMatches = matchesSnapshot.docs.filter((docSnap) => {
    const data = docSnap.data() || {};
    const lastHeartbeat = toMillis(data.reservationLastHeartbeatAt) || toMillis(data.reservedAt) || 0;
    return !!lastHeartbeat && now - lastHeartbeat >= MATCH_RESERVATION_STALE_MS;
  });

  await Promise.all(staleMatches.map(docSnap => removeStaleReservedMatch({db, matchRef: docSnap.ref, now})));
}
